import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpTable, Modal } from "../../components/erp";
import { errorText } from "../../api/client";
import { analysisRequiredApi, AnalysisRequiredRow, labNamesApi, LabNameRow } from "./api";

const AddModal: React.FC<{ title: string; fields: { key: string; label: string }[]; onClose: () => void; onSubmit: (v: Record<string, string>) => Promise<{ Error: string | null }>; testId: string }> = ({ title, fields, onClose, onSubmit, testId }) => {
  const [v, setV] = useState<Record<string, string>>({}); const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try { const r = await onSubmit(v); if (r.Error) setErr(r.Error); else onClose(); }
    catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={title} onClose={onClose} testId={testId}>
      <form onSubmit={submit} data-testid={`${testId}-form`}>
        {fields.map((f) => (
          <div className="mb-3" key={f.key}><label className="form-label">{f.label}</label>
            <input className="form-control" value={v[f.key] || ""} onChange={(e) => setV((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`${testId}-${f.key}`} /></div>
        ))}
        {err && <div className="alert alert-danger" data-testid={`${testId}-error`}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid={`${testId}-submit`}>{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of ApiV2/QualityControl/Controller/LabNamesMastersController.php — table QcEiaLabNames. */
export function LabNamesMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["qc", "lab-names"], queryFn: labNamesApi.list });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Lab Names" location="Quality Control" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-lab"><i className="fa fa-plus" /> New Lab</button></div>
        <ErpTable<LabNameRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="lab-names-table"
          columns={[{ key: "SNO", title: "S.No" }, { key: "LabName", title: "Lab Name" }, { key: "Address", title: "Address" }, { key: "Description", title: "Description" }]} />
      </div></div>
      {open && <AddModal title="New Lab" testId="lab-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["qc", "lab-names"] }); }}
        fields={[{ key: "LabName", label: "Lab Name" }, { key: "Address", label: "Address" }, { key: "Description", label: "Description" }]}
        onSubmit={(v) => labNamesApi.post(v)} />}
    </>
  );
}

/** Port of ApiV2/QualityControl/Controller/AnalysisRequiredMastersController.php — table QcTestList. */
export function AnalysisRequiredMaster() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["qc", "analysis-required"], queryFn: analysisRequiredApi.list });
  const [open, setOpen] = useState(false);
  return (
    <>
      <PageHeader title="Analysis Required" location="Quality Control" />
      <div className="card"><div className="card-body">
        <div className="text-center mb-3"><button type="button" className="btn btn-success" onClick={() => setOpen(true)} data-testid="btn-new-test"><i className="fa fa-plus" /> New Test</button></div>
        <ErpTable<AnalysisRequiredRow> rows={list.data?.Data || []} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="analysis-required-table"
          columns={[{ key: "SNO", title: "S.No" }, { key: "TestCategory", title: "Category" }, { key: "TestName", title: "Test Name" }]} />
      </div></div>
      {open && <AddModal title="New Test" testId="test-modal" onClose={() => { setOpen(false); qc.invalidateQueries({ queryKey: ["qc", "analysis-required"] }); }}
        fields={[{ key: "TestCategory", label: "Category" }, { key: "TestName", label: "Test Name" }]}
        onSubmit={(v) => analysisRequiredApi.post(v)} />}
    </>
  );
}
